import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiUserPlus, FiEdit2, FiMinus, FiSearch, FiPlus } from 'react-icons/fi';
import ItemCard from '../components/pos/ItemCard';
import EditItemModal from '../components/pos/EditItemModal';

export default function QuoteForm() {
  const emptyAddress = { line1: '', line2: '', city: '', state: '', pincode: '' };
  const normalizeAddress = (address) => ({
    line1: String(address?.line1 || '').trim(),
    line2: String(address?.line2 || '').trim(),
    city: String(address?.city || '').trim(),
    state: String(address?.state || '').trim(),
    pincode: String(address?.pincode || '').trim(),
  });
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuth();
  const companyId = company?.id || (typeof window !== 'undefined' ? localStorage.getItem('companyId') : undefined);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [posStateCode, setPosStateCode] = useState('');
  const INDIAN_STATES = [
    { code: '01', name: 'Jammu & Kashmir' },
    { code: '02', name: 'Himachal Pradesh' },
    { code: '03', name: 'Punjab' },
    { code: '04', name: 'Chandigarh' },
    { code: '05', name: 'Uttarakhand' },
    { code: '06', name: 'Haryana' },
    { code: '07', name: 'Delhi' },
    { code: '08', name: 'Rajasthan' },
    { code: '09', name: 'Uttar Pradesh' },
    { code: '10', name: 'Bihar' },
    { code: '11', name: 'Sikkim' },
    { code: '12', name: 'Arunachal Pradesh' },
    { code: '13', name: 'Nagaland' },
    { code: '14', name: 'Manipur' },
    { code: '15', name: 'Mizoram' },
    { code: '16', name: 'Tripura' },
    { code: '17', name: 'Meghalaya' },
    { code: '18', name: 'Assam' },
    { code: '19', name: 'West Bengal' },
    { code: '20', name: 'Jharkhand' },
    { code: '21', name: 'Odisha' },
    { code: '22', name: 'Chhattisgarh' },
    { code: '23', name: 'Madhya Pradesh' },
    { code: '24', name: 'Gujarat' },
    { code: '26', name: 'Daman and Diu, Dadra & Nagar Haveli' },
    { code: '27', name: 'Maharashtra' },
    { code: '28', name: 'Andhra Pradesh (before bifurcation)' },
    { code: '36', name: 'Telangana' },
    { code: '37', name: 'Andhra Pradesh (New)' },
    { code: '29', name: 'Karnataka' },
    { code: '30', name: 'Goa' },
    { code: '31', name: 'Lakshadweep' },
    { code: '32', name: 'Kerala' },
    { code: '33', name: 'Tamil Nadu' },
    { code: '34', name: 'Puducherry' },
    { code: '35', name: 'Andaman & Nicobar Islands' },
    { code: '38', name: 'Ladakh' }
  ];
  const [form, setForm] = useState({ customerId: '', date: new Date().toISOString().slice(0,10), terms: '', notes: '' });
  const [lines, setLines] = useState([{ itemId: '', description: '', quantity: null, unit: '', price: null, discountAmount: null, discountPercent: null, cgstRate: null, sgstRate: null, igstRate: null, cessRate: null }]);
  const [computed, setComputed] = useState({ taxableValue: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0, cessAmount: 0, total: 0, supplyType: 'INTRA' });
  
  const [showNewItem, setShowNewItem] = useState(false);
  const [newItemAssignToIndex, setNewItemAssignToIndex] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
  const [gst, setGst] = useState({ checked: false, hasGSTIN: true, hideGST: false, gstinInput: '' });
  const [customerGst, setCustomerGst] = useState({ gstinInput: '' });
  const [editingItemId, setEditingItemId] = useState(null);
  // POS-like item browser (search + filter + cards)
  const [itemSearch, setItemSearch] = useState('');
  const [itemType, setItemType] = useState('all'); // all | product | service
  const [pulseItemId, setPulseItemId] = useState(null);
  // Quote-level discount (like POS)
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' | 'percent'
  // Summary discount editor (POS-style "+ Disc")
  const [openDisc, setOpenDisc] = useState({});
  // Customer create/edit UI state (right panel)
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '', gstin: '', address: emptyAddress });
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [editCustomer, setEditCustomer] = useState({ id: '', name: '', email: '', phone: '', gstin: '', address: emptyAddress });
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const controlStyle = { fontSize: 12, padding: '6px 8px', height: 36, minHeight: 36 };
  const labelStyle = { fontSize: 12, color: '#374151' };
  const smallBtn = { fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center' };
  // Defaults for Terms/Notes for future documents
  const [defaults, setDefaults] = useState({ loaded: false, exists: false, terms: '', notes: '' });
  const saveDocDefaults = async () => {
    if (!companyId) return;
    const payload = { terms: form.terms || null, notes: form.notes || null };
    const { data } = await api.put(`/api/companies/${companyId}/document-defaults/QUOTE`, payload);
    const rec = data?.defaults || null;
    setDefaults({ loaded: true, exists: !!rec, terms: rec?.terms || '', notes: rec?.notes || '' });
  };
  const saveCompanyGST = async () => {
    try {
      const toSend = (gst.gstinInput || '').trim();
      if (!toSend) return;
      const { data } = await api.post(`/api/companies/${companyId}/gst`, { gstin: toSend });
      setGst({ checked: true, hasGSTIN: true, hideGST: false, gstinInput: '', showInput: false });
    } catch (e) { console.error('Failed to save GSTIN', e); }
  };
  const saveCustomerGST = async () => {
    try {
      const toSend = (customerGst.gstinInput || '').trim();
      if (!toSend || !form.customerId) return;
      const { data: updated } = await api.post(`/api/companies/${companyId}/customers/${form.customerId}/gst`, { gstin: toSend });
      setCustomerGst({ gstinInput: '' });
      setCustomers(prev => prev.map(c => String(c.id) === String(form.customerId) ? { ...c, gstin: updated.gstin, stateCode: updated.stateCode } : c));
    } catch (e) { console.error('Failed to save customer GSTIN', e); }
  };

  // Helpers for customer create/edit similar to POS
  const isValidEmail = (val) => {
    if (!val) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(val).trim());
  };
  const isValidPhone10 = (val) => {
    if (!val) return true;
    return /^\d{10}$/.test(String(val).trim());
  };
  const newCustEmailInvalid = !!newCustomer.email && !isValidEmail(newCustomer.email);
  const newCustPhoneInvalid = !!newCustomer.phone && !isValidPhone10(newCustomer.phone);
  const editCustEmailInvalid = !!editCustomer.email && !isValidEmail(editCustomer.email);
  const editCustPhoneInvalid = !!editCustomer.phone && !isValidPhone10(editCustomer.phone);
  const resolvePosFromCustomer = (cust) => {
    const stateCode = String(cust?.stateCode || '').trim();
    if (/^\d{2}$/.test(stateCode)) return stateCode;

    const stateValue = String(cust?.address?.state || '').trim().toLowerCase();
    if (stateValue) {
      const match = INDIAN_STATES.find((s) => stateValue.includes(String(s.name || '').toLowerCase()) || String(s.name || '').toLowerCase().includes(stateValue));
      if (match?.code) return match.code;
    }

    const pincode = String(cust?.address?.pincode || '').trim();
    const pin2 = pincode.slice(0, 2);
    const pincodePrefixMap = {
      '11': '07', '12': '06', '13': '06', '14': '03', '15': '03', '16': '03', '17': '02', '18': '01', '19': '04',
      '20': '09', '21': '09', '22': '09', '23': '09', '24': '09', '25': '09', '26': '09', '27': '09', '28': '09',
      '30': '08', '31': '08', '32': '08', '33': '08', '34': '08',
      '36': '24', '37': '24', '38': '24', '39': '24',
      '40': '27', '41': '27', '42': '27', '43': '27', '44': '27',
      '45': '23', '46': '23', '47': '23', '48': '23',
      '49': '22',
      '50': '36', '51': '37', '52': '37', '53': '37',
      '56': '29', '57': '29', '58': '29', '59': '29',
      '60': '33', '61': '33', '62': '33', '63': '33',
      '64': '32', '67': '32', '68': '32', '69': '32',
      '70': '19', '71': '19', '72': '19', '73': '19', '74': '19',
      '75': '21', '76': '21', '77': '21',
      '78': '18', '79': '18',
      '80': '10', '81': '10', '82': '10', '83': '20', '84': '20', '85': '20',
      '90': '13', '91': '13', '92': '13', '93': '14', '94': '15', '95': '12', '96': '16', '97': '17', '98': '11'
    };
    return pincodePrefixMap[pin2] || '';
  };
  const reloadCustomers = async () => {
    try {
      const { data } = await api.get('/api/companies/' + companyId + '/customers');
      setCustomers(data?.customers || []);
    } catch (e) {
      console.error('Failed to reload customers', e);
    }
  };
  const handleCreateCustomer = async () => {
    if (!companyId) return;
    if (!newCustomer.name.trim()) return;
    setCreatingCustomer(true);
    try {
      const payload = { ...newCustomer, address: normalizeAddress(newCustomer.address), companyId: Number(companyId), stateCode: companyData?.stateCode || '' };
      const { data } = await api.post('/api/customers', payload);
      await reloadCustomers();
      const createdId = data?.customer?.id || data?.id;
      if (createdId) setForm((prev) => ({ ...prev, customerId: String(createdId) }));
      setShowCreateCustomer(false);
      setNewCustomer({ name: '', email: '', phone: '', gstin: '', address: emptyAddress });
    } catch (e) {
      console.error('Failed to create customer', e);
    } finally {
      setCreatingCustomer(false);
    }
  };
  const handleOpenEditCustomer = () => {
    const cust = customers.find((c) => String(c.id) === String(form.customerId));
    if (!cust) return;
    setEditCustomer({ id: cust.id, name: cust.name || '', email: cust.email || '', phone: cust.phone || '', gstin: cust.gstin || '', address: normalizeAddress(cust.address) });
    setShowEditCustomer(true);
    setShowCreateCustomer(false);
  };
  const handleSaveEditCustomer = async () => {
    if (!editCustomer.id) return;
    try {
      const payload = { name: editCustomer.name, email: editCustomer.email, phone: editCustomer.phone, gstin: editCustomer.gstin, address: normalizeAddress(editCustomer.address), stateCode: companyData?.stateCode || '' };
      if (companyId) {
        await api.put(`/api/companies/${companyId}/customers/${editCustomer.id}`, payload);
      } else {
        await api.put(`/api/customers/${editCustomer.id}`, payload);
      }
      setCustomers((prev) => prev.map((c) => String(c.id) === String(editCustomer.id) ? { ...c, ...payload } : c));
      await reloadCustomers();
      setShowEditCustomer(false);
    } catch (e) {
      console.error('Failed to update customer', e);
    }
  };


  const clamp = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return num < 0 ? 0 : num;
  };

  const splitIgstRate = (igstRate) => {
    const ig = Number(igstRate || 0);
    if (!Number.isFinite(ig) || ig <= 0) return { cg: 0, sg: 0 };
    const half = +((ig / 2)).toFixed(2);
    // Keep sum stable (avoid floating drift)
    return { cg: half, sg: +(ig - half).toFixed(2) };
  };

  const normalizeStateCode = (code) => {
    const s = String(code ?? '').trim();
    if (!s) return '';
    // State codes are 2-digit strings ('07'). Backend can sometimes return numbers.
    return s.padStart(2, '0');
  };

  const inferIgstRateFromLineOrItem = (line, item) => {
    const lineIg = Number(line?.igstRate || 0);
    if (Number.isFinite(lineIg) && lineIg > 0) return lineIg;
    const itemIg = Number(item?.igstRate || 0);
    if (Number.isFinite(itemIg) && itemIg > 0) return itemIg;
    const itemCg = Number(item?.cgstRate || 0);
    const itemSg = Number(item?.sgstRate || 0);
    const itemSum = itemCg + itemSg;
    if (Number.isFinite(itemSum) && itemSum > 0) return itemSum;
    const lineCg = Number(line?.cgstRate || 0);
    const lineSg = Number(line?.sgstRate || 0);
    const lineSum = lineCg + lineSg;
    if (Number.isFinite(lineSum) && lineSum > 0) return lineSum;
    return 0;
  };

  const docDiscountAmount = useMemo(() => {
    const base = Number(computed?.netBeforeDocDiscount ?? 0);
    const raw = Number(discount || 0);
    if (discountType === 'percent') {
      const pct = Math.max(0, Math.min(100, raw));
      return +((base * pct) / 100).toFixed(2);
    }
    return +Math.max(0, raw).toFixed(2);
  }, [discount, discountType, computed?.netBeforeDocDiscount]);

  const filteredItems = useMemo(() => {
    const q = (itemSearch || '').trim().toLowerCase();
    return (items || []).filter((it) => {
      if (itemType === 'service' && !it.isService) return false;
      if (itemType === 'product' && it.isService) return false;
      if (!q) return true;
      return (
        (it.name || '').toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        (it.hsnsac || '').toLowerCase().includes(q)
      );
    });
  }, [items, itemSearch, itemType]);

  const quoteQtyForItem = useMemo(() => {
    const map = new Map();
    for (const l of (lines || [])) {
      const id = l?.itemId ? String(l.itemId) : '';
      if (!id) continue;
      const qty = Number(l.quantity || 0);
      map.set(id, (map.get(id) || 0) + qty);
    }
    return map;
  }, [lines]);

  const addOrIncItemToQuote = (it) => {
    if (!it?.id) return;
    const idStr = String(it.id);
    const pos = normalizeStateCode(posStateCode);
    const companyState = normalizeStateCode(companyData?.stateCode);
    const isInter = companyState && pos && companyState !== pos;
    setLines((prev) => {
      const idx = (prev || []).findIndex((l) => String(l.itemId || '') === idStr);
      if (idx >= 0) {
        return (prev || []).map((l, i) => {
          if (i !== idx) return l;
          const nextQty = Math.max(0, Number(l.quantity || 0) + 1);
          return { ...l, quantity: nextQty };
        });
      }
      const cg = Number(it.cgstRate || 0);
      const sg = Number(it.sgstRate || 0);
      const inferredIG = cg + sg;
      const ig = inferIgstRateFromLineOrItem(null, it) || inferredIG;
      const taxes = gst.hideGST
        ? { cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0 }
        : (isInter
          ? { cgstRate: 0, sgstRate: 0, igstRate: ig, cessRate: Number(it.cessRate || 0) }
          : { cgstRate: cg, sgstRate: sg, igstRate: inferredIG, cessRate: Number(it.cessRate || 0) });
      return [
        ...(prev || []),
        {
          itemId: it.id,
          description: it.description || '',
          quantity: 1,
          unit: it.unit || '',
          price: it.price ?? 0,
          discountAmount: 0,
          discountPercent: 0,
          ...taxes
        }
      ];
    });
    setPulseItemId(it.id);
  };

  const decItemInQuote = (it) => {
    if (!it?.id) return;
    const idStr = String(it.id);
    setLines((prev) => {
      const arr = [...(prev || [])];
      const idx = arr.findIndex((l) => String(l.itemId || '') === idStr);
      if (idx < 0) return prev;
      const curQty = Number(arr[idx].quantity || 0);
      const nextQty = Math.max(0, curQty - 1);
      if (nextQty <= 0) return arr.filter((_, i) => i !== idx);
      arr[idx] = { ...arr[idx], quantity: nextQty };
      return arr;
    });
    setPulseItemId(it.id);
  };

  useEffect(() => {
    (async () => {
      const { data: custs } = await api.get('/api/companies/' + companyId + '/customers');
      setCustomers(custs?.customers || []);
      const { data: its } = await api.get('/api/items', { params: { companyId } });
      setItems(its);
      const { data: comp } = await api.get('/api/companies/' + companyId);
      setCompanyData(comp);
      if (!params.id && !posStateCode && comp?.stateCode) setPosStateCode(comp.stateCode);
      try {
        const { data: gstStatus } = await api.get(`/api/companies/${companyId}/gst-status`);
        setCompanyData(prev => ({ ...prev, gstin: gstStatus.gstin ?? prev?.gstin, stateCode: gstStatus.stateCode ?? prev?.stateCode }));
        const effectiveState = gstStatus.stateCode || comp?.stateCode || '';
        if (!params.id && !posStateCode && effectiveState) setPosStateCode(effectiveState);
        setGst({ checked: true, hasGSTIN: !!gstStatus.hasGSTIN, hideGST: !gstStatus.hasGSTIN, gstinInput: '' });
      } catch (_) { setGst(prev => ({ ...prev, checked: true })); }
      if (params.id) {
        const { data: q } = await api.get(`/api/quotes/${params.id}`);
        setQuoteNumber(q?.number || '');
        setForm({ customerId: q.customerId, date: q.date, terms: q.terms || '', notes: q.notes || '' });
        if (q.posStateCode) setPosStateCode(q.posStateCode);
        setLines((q.items || []).map(l => ({ itemId: l.itemId || '', description: l.description || '', quantity: l.quantity, unit: l.unit, price: l.price, discountAmount: l.discountAmount, discountPercent: l.discountPercent, cgstRate: l.cgstRate, sgstRate: l.sgstRate, igstRate: l.igstRate, cessRate: l.cessRate })));
        // Load document-level discount
        try {
          const qPct = Number(q?.discountPercent || 0);
          const qAmt = Number(q?.discountAmount || 0);
          if (qPct > 0) { setDiscountType('percent'); setDiscount(qPct); }
          else if (qAmt > 0) { setDiscountType('amount'); setDiscount(qAmt); }
        } catch (_) { /* no-op */ }
        // If the existing quote has any tax rates, always unhide GST to preserve them
        try {
          const hadAnyTax = (q.items || []).some(itm => Number(itm.cgstRate || 0) > 0 || Number(itm.sgstRate || 0) > 0 || Number(itm.igstRate || 0) > 0 || Number(itm.cessRate || 0) > 0);
          if (hadAnyTax) setGst(prev => ({ ...prev, checked: true, hideGST: false }));
        } catch (_) { /* no-op */ }
      } else {
        const qs = new URLSearchParams(location.search);
        const fromId = qs.get('from');
        if (fromId) {
          try {
            const { data: src } = await api.get(`/api/quotes/${fromId}`);
            setForm({ customerId: src.customerId, date: new Date().toISOString().slice(0,10), terms: src.terms || '', notes: src.notes || '' });
            if (src.posStateCode) setPosStateCode(src.posStateCode);
            setLines((src.items || []).map(l => ({ itemId: l.itemId || '', description: l.description || '', quantity: l.quantity, unit: l.unit, price: l.price, discountAmount: l.discountAmount, discountPercent: l.discountPercent, cgstRate: l.cgstRate, sgstRate: l.sgstRate, igstRate: l.igstRate, cessRate: l.cessRate })));
            // Load document-level discount from source
            try {
              const srcPct = Number(src?.discountPercent || 0);
              const srcAmt = Number(src?.discountAmount || 0);
              if (srcPct > 0) { setDiscountType('percent'); setDiscount(srcPct); }
              else if (srcAmt > 0) { setDiscountType('amount'); setDiscount(srcAmt); }
            } catch (_) { /* no-op */ }
            // If the source quote has any tax rates, always unhide GST to preserve them
            try {
              const hadAnyTax = (src.items || []).some(itm => Number(itm.cgstRate || 0) > 0 || Number(itm.sgstRate || 0) > 0 || Number(itm.igstRate || 0) > 0 || Number(itm.cessRate || 0) > 0);
              if (hadAnyTax) setGst(prev => ({ ...prev, checked: true, hideGST: false }));
            } catch (_) { /* no-op */ }
          } catch (e) { /* ignore prefill errors */ }
        } else {
          // Prepopulate from saved defaults if available (only for new quote)
          try {
            const { data } = await api.get(`/api/companies/${companyId}/document-defaults/QUOTE`);
            const rec = data?.defaults || null;
            if (rec) {
              setDefaults({ loaded: true, exists: true, terms: rec.terms || '', notes: rec.notes || '' });
              setForm(prev => ({
                ...prev,
                terms: prev.terms || rec.terms || '',
                notes: prev.notes || rec.notes || ''
              }));
            } else {
              setDefaults({ loaded: true, exists: false, terms: '', notes: '' });
            }
          } catch (_) {
            setDefaults(prev => ({ ...prev, loaded: true }));
          }
        }
      }
    })();
  }, [companyId, location.search, params.id]);

  useEffect(() => {
    if (!form.customerId) return;
    const cust = customers.find((c) => String(c.id) === String(form.customerId));
    if (!cust) return;
    const nextPos = resolvePosFromCustomer(cust);
    if (nextPos && nextPos !== posStateCode) setPosStateCode(nextPos);
  }, [form.customerId, customers]);

  const updateLine = (idx, patch) => {
    let next = lines.map((l, i) => i === idx ? { ...l, ...patch } : l);
    if (patch.itemId != null) {
      const it = items.find(x => String(x.id) === String(patch.itemId));
      if (it) {
        // Determine supply type to set correct taxes from item defaults
        const pos = normalizeStateCode(posStateCode);
        const companyState = normalizeStateCode(companyData?.stateCode);
        const isInter = companyState && pos && companyState !== pos;
        next = next.map((l, i) => {
          if (i !== idx) return l;
          const base = {
            ...l,
            description: it.description || '',
            unit: it.unit || '',
            price: it.price ?? 0,
            cessRate: it.cessRate ?? 0
          };
          if (gst.hideGST) {
            return { ...base, cgstRate: 0, sgstRate: 0, igstRate: 0 };
          }
          if (isInter) {
            const ig = (it.igstRate != null ? Number(it.igstRate) : (Number(it.cgstRate || 0) + Number(it.sgstRate || 0)));
            return { ...base, cgstRate: 0, sgstRate: 0, igstRate: ig };
          }
          const cg = Number(it.cgstRate || 0);
          const sg = Number(it.sgstRate || 0);
          return { ...base, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
        });
      }
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'cgstRate') && !Object.prototype.hasOwnProperty.call(patch, 'sgstRate')) {
      next = next.map((l, i) => {
        if (i !== idx) return l;
        const prev = lines[i];
        const prevCgst = Number(prev.cgstRate || 0);
        const prevSgstRaw = prev.sgstRate;
        const newCgst = Number(patch.cgstRate || 0);
        const shouldSync = prevSgstRaw === '' || prevSgstRaw == null || Number(prevSgstRaw) === prevCgst;
        return shouldSync ? { ...l, sgstRate: newCgst } : l;
      });
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'sgstRate') && !Object.prototype.hasOwnProperty.call(patch, 'cgstRate')) {
      next = next.map((l, i) => {
        if (i !== idx) return l;
        const prev = lines[i];
        const prevSgst = Number(prev.sgstRate || 0);
        const prevCgstRaw = prev.cgstRate;
        const newSgst = Number(patch.sgstRate || 0);
        const shouldSync = prevCgstRaw === '' || prevCgstRaw == null || Number(prevCgstRaw) === prevSgst;
        return shouldSync ? { ...l, cgstRate: newSgst } : l;
      });
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'cgstRate') || Object.prototype.hasOwnProperty.call(patch, 'sgstRate')) {
      next = next.map((l, i) => i === idx ? { ...l, igstRate: Number(l.cgstRate || 0) + Number(l.sgstRate || 0) } : l);
    }
    setLines(next);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const save = async () => {
    const selectedCustomer = customers.find(c => String(c.id) === String(form.customerId));
    // Filter out blank lines (no itemId or quantity <= 0) before processing
    const validLines = (lines || []).filter((l) => l.itemId && Number(l.quantity || 0) > 0);
    // Apply document-level discount BEFORE tax by distributing it across lines (like POS checkout)
    const prepared = validLines.map((l) => {
      const qty = (l.quantity === '' || l.quantity == null) ? 0 : Number(l.quantity);
      const price = (l.price === '' || l.price == null) ? 0 : Number(l.price);
      const gross = qty * price;
      const discAmt = (l.discountAmount === '' || l.discountAmount == null) ? 0 : Math.max(0, Number(l.discountAmount));
      const discPct = (l.discountPercent === '' || l.discountPercent == null) ? 0 : Math.max(0, Math.min(100, Number(l.discountPercent)));
      const discountFromPercent = (discPct / 100) * gross;
      const lineDisc = Math.min(gross, discAmt + discountFromPercent);
      const netBase = Math.max(0, gross - lineDisc);
      return { l, qty, price, gross, lineDisc: +lineDisc.toFixed(2), netBase: +netBase.toFixed(2) };
    });
    const netTotal = +prepared.reduce((acc, x) => acc + Number(x.netBase || 0), 0).toFixed(2);
    const docDisc = (() => {
      const raw = Number(discount || 0);
      if (discountType === 'percent') {
        const pct = Math.max(0, Math.min(100, raw));
        return +((netTotal * pct) / 100).toFixed(2);
      }
      return +Math.max(0, raw).toFixed(2);
    })();
    const itemsPayload = prepared.map((x) => {
      const l = x.l;
      // Check if line already has tax rates - preserve them even if hideGST
      const lineHasTax = Number(l.cgstRate || 0) > 0 || Number(l.sgstRate || 0) > 0 || Number(l.igstRate || 0) > 0;
      const shouldZeroTax = gst.hideGST && !lineHasTax;
      return ({
        itemId: Number(l.itemId),
        description: l.description || '',
        quantity: x.qty,
        unit: l.unit || '',
        price: x.price,
        // Store ONLY item-level discount; doc discount is stored separately on Quote and distributed by backend
        discountAmount: x.lineDisc,
        discountPercent: 0,
        cgstRate: shouldZeroTax ? 0 : (l.cgstRate === '' || l.cgstRate == null ? 0 : Number(l.cgstRate)),
        sgstRate: shouldZeroTax ? 0 : (l.sgstRate === '' || l.sgstRate == null ? 0 : Number(l.sgstRate)),
        igstRate: shouldZeroTax ? 0 : (l.igstRate === '' || l.igstRate == null ? 0 : Number(l.igstRate)),
        cessRate: shouldZeroTax ? 0 : (l.cessRate === '' || l.cessRate == null ? 0 : Number(l.cessRate))
      });
    });
    // Build document-level discount for storage (separate from line distribution)
    const docDiscPayload = (() => {
      const raw = Number(discount || 0);
      if (discountType === 'percent') return { discountAmount: 0, discountPercent: Math.max(0, Math.min(100, raw)) };
      return { discountAmount: Math.max(0, raw), discountPercent: 0 };
    })();
    const payload = { companyId: Number(companyId), customerId: Number(form.customerId), date: form.date, terms: form.terms, notes: form.notes, items: itemsPayload, posStateCode: posStateCode || null, ...docDiscPayload };
    const toSafeName = (val) => String(val || '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const buildPdfName = (numOrId) => `quote-${toSafeName(numOrId) || 'quote'}.pdf`;
    if (params.id) {
      const { data: updated } = await api.put(`/api/quotes/${params.id}`, payload);
      const nextNumber = updated?.number || quoteNumber || params.id;
      if (updated?.number) setQuoteNumber(updated.number);
      try {
        const resp = await api.get(`/api/quotes/${params.id}/pdf`, { responseType: 'blob' });
        const pdfUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        const win = window.open(pdfUrl, '_blank', 'noopener');
        if (!win) {
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = buildPdfName(nextNumber);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 10000);
      } catch (e) {
        console.error('Failed to open PDF', e);
      }
      navigate('/quotes');
    } else {
      const { data } = await api.post('/api/quotes', payload);
      const nextNumber = data?.number || data?.quote?.number || data?.id;
      if (data?.number) setQuoteNumber(data.number);
      try {
        const resp = await api.get(`/api/quotes/${data.id}/pdf`, { responseType: 'blob' });
        const pdfUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        const win = window.open(pdfUrl, '_blank', 'noopener');
        if (!win) {
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = buildPdfName(nextNumber);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 10000);
      } catch (e) {
        console.error('Failed to open PDF', e);
      }
      navigate('/quotes');
    }
  };

  useEffect(() => {
    const pos = normalizeStateCode(posStateCode);
    const companyState = normalizeStateCode(companyData?.stateCode);
    const isInter = companyState && pos && companyState !== pos;
    const noGst = !companyData?.gstin;
    // First pass: compute gross + line discounts + net base (before doc discount)
    const prepared = [];
    let grossTotal = 0;
    let lineDiscountTotal = 0;
    let netBeforeDocDiscount = 0;
    for (const l of (lines || [])) {
      const it = items.find(x => String(x.id) === String(l.itemId));
      const price = Number(l.price || 0);
      const qty = Number(l.quantity || 0);
      const gross = qty * price;
      const discAmt = Math.max(0, Number(l.discountAmount || 0));
      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
      const discountFromPercent = (discPct / 100) * gross;
      const lineDisc = Math.min(gross, discAmt + discountFromPercent);
      const netBase = Math.max(0, gross - lineDisc);
      grossTotal += gross;
      lineDiscountTotal += lineDisc;
      netBeforeDocDiscount += netBase;
      prepared.push({ l, it, netBase });
    }
    grossTotal = +grossTotal.toFixed(2);
    lineDiscountTotal = +lineDiscountTotal.toFixed(2);
    netBeforeDocDiscount = +netBeforeDocDiscount.toFixed(2);

    // Doc discount amount (amount or percent) applied BEFORE tax
    const docDisc = (() => {
      const raw = Number(discount || 0);
      if (discountType === 'percent') {
        const pct = Math.max(0, Math.min(100, raw));
        return +((netBeforeDocDiscount * pct) / 100).toFixed(2);
      }
      return +Math.max(0, raw).toFixed(2);
    })();
    const taxableAfterDoc = Math.max(0, +(netBeforeDocDiscount - docDisc).toFixed(2));
    const factor = netBeforeDocDiscount > 0 ? +(taxableAfterDoc / netBeforeDocDiscount).toFixed(6) : 1;

    // Second pass: compute taxes on scaled net base
    // Use a running total approach to avoid rounding errors - last item gets the remainder
    let taxableValue = 0, cgstAmount = 0, sgstAmount = 0, igstAmount = 0, cessAmount = 0;
    let allocatedTaxable = 0;
    for (let i = 0; i < prepared.length; i++) {
      const row = prepared[i];
      const l = row.l;
      const it = row.it;
      const isLast = i === prepared.length - 1;
      // For last item, allocate the remaining taxable to avoid rounding errors
      const netBeforeTax = isLast
        ? +(taxableAfterDoc - allocatedTaxable).toFixed(2)
        : +(Number(row.netBase || 0) * factor).toFixed(2);
      allocatedTaxable += netBeforeTax;
      const cgstRate = noGst || isInter ? 0 : Number(((l.cgstRate === '' || l.cgstRate == null) ? (it?.cgstRate ?? 0) : l.cgstRate));
      const sgstRate = noGst || isInter ? 0 : Number(((l.sgstRate === '' || l.sgstRate == null) ? (it?.sgstRate ?? 0) : l.sgstRate));
      const inferredIG = Number(((l.cgstRate === '' || l.cgstRate == null) ? (it?.cgstRate ?? 0) : l.cgstRate)) + Number(((l.sgstRate === '' || l.sgstRate == null) ? (it?.sgstRate ?? 0) : l.sgstRate));
      const igstRate = noGst || !isInter ? 0 : Number(((l.igstRate === '' || l.igstRate == null) ? (it?.igstRate ?? inferredIG) : l.igstRate));
      const cessRate = Number(((l.cessRate === '' || l.cessRate == null) ? (it?.cessRate ?? 0) : l.cessRate));
      // Don't round individual line taxes during accumulation - only round the final totals
      const cgst = netBeforeTax * cgstRate / 100;
      const sgst = netBeforeTax * sgstRate / 100;
      const igst = netBeforeTax * igstRate / 100;
      const cess = netBeforeTax * cessRate / 100;
      taxableValue += netBeforeTax; cgstAmount += cgst; sgstAmount += sgst; igstAmount += igst; cessAmount += cess;
    }
    const discountTotal = +(lineDiscountTotal + docDisc).toFixed(2);
    // Round all totals at the end to avoid accumulated rounding errors
    const roundedTaxable = +taxableValue.toFixed(2);
    const roundedCgst = +cgstAmount.toFixed(2);
    const roundedSgst = +sgstAmount.toFixed(2);
    const roundedIgst = +igstAmount.toFixed(2);
    const roundedCess = +cessAmount.toFixed(2);
    const grandTotal = +(roundedTaxable + roundedCgst + roundedSgst + roundedIgst + roundedCess).toFixed(2);
    setComputed({
      grossTotal,
      lineDiscountTotal,
      docDiscount: docDisc,
      netBeforeDocDiscount,
      taxableValue: roundedTaxable,
      cgstAmount: roundedCgst,
      sgstAmount: roundedSgst,
      igstAmount: roundedIgst,
      cessAmount: roundedCess,
      total: grandTotal,
      discountTotal,
      supplyType: isInter ? 'INTER' : 'INTRA'
    });
  }, [lines, form.customerId, companyData, customers, items, posStateCode, discount, discountType]);

  // Sync line tax fields when supply type or GST visibility changes
  useEffect(() => {
    const pos = normalizeStateCode(posStateCode);
    const companyState = normalizeStateCode(companyData?.stateCode);
    const isInter = companyState && pos && companyState !== pos;
    setLines(prev => prev.map(l => {
      const it = items.find(x => String(x.id) === String(l.itemId));
      if (!it) return l;
      // If GST is hidden (e.g. no GSTIN), preserve any existing line taxes.
      const lineHasTax = Number(l.cgstRate || 0) > 0 || Number(l.sgstRate || 0) > 0 || Number(l.igstRate || 0) > 0;
      if (gst.hideGST) return lineHasTax ? l : { ...l, cgstRate: 0, sgstRate: 0, igstRate: 0 };
      // Only fill defaults if the line does not already have explicit rates
      const hasAnyExplicit = (l.cgstRate != null && l.cgstRate !== '') || (l.sgstRate != null && l.sgstRate !== '') || (l.igstRate != null && l.igstRate !== '');
      if (hasAnyExplicit) {
        // For interstate, ensure CGST/SGST are zeroed if explicit IGST is set
        if (isInter) {
          const ig = inferIgstRateFromLineOrItem(l, it);
          return { ...l, cgstRate: 0, sgstRate: 0, igstRate: ig };
        }
        // For intrastate, ensure IGST reflects sum of CGST+SGST; if coming from interstate (igst>0 and cgst/sgst empty/zero), fallback to item defaults
        const hadIgst = Number(l.igstRate || 0) > 0;
        const cgEmptyOrZero = l.cgstRate == null || l.cgstRate === '' || Number(l.cgstRate) === 0;
        const sgEmptyOrZero = l.sgstRate == null || l.sgstRate === '' || Number(l.sgstRate) === 0;
        const shouldFallbackToItem = hadIgst && cgEmptyOrZero && sgEmptyOrZero;
        const deriveCgstSgst = () => {
          if (!shouldFallbackToItem) {
            return {
              cg: (l.cgstRate != null && l.cgstRate !== '') ? Number(l.cgstRate) : Number(it.cgstRate || 0),
              sg: (l.sgstRate != null && l.sgstRate !== '') ? Number(l.sgstRate) : Number(it.sgstRate || 0),
            };
          }
          const itemCg = Number(it.cgstRate || 0);
          const itemSg = Number(it.sgstRate || 0);
          if (itemCg > 0 || itemSg > 0) return { cg: itemCg, sg: itemSg };
          // Some item masters/lines only store IGST; split the best available IGST-like value for intra-state display
          const srcIg = inferIgstRateFromLineOrItem(l, it);
          return splitIgstRate(srcIg);
        };
        const { cg, sg } = deriveCgstSgst();
        return { ...l, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
      }
      if (isInter) {
        const ig = inferIgstRateFromLineOrItem(l, it);
        return { ...l, cgstRate: 0, sgstRate: 0, igstRate: ig };
      }
      let cg = Number(it.cgstRate || 0);
      let sg = Number(it.sgstRate || 0);
      if ((cg === 0 && sg === 0)) {
        const srcIg = inferIgstRateFromLineOrItem(l, it);
        const split = splitIgstRate(srcIg);
        cg = split.cg; sg = split.sg;
      }
      return { ...l, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
    }));
  }, [gst.hideGST, form.customerId, posStateCode, companyData, customers, items]);

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>{params.id ? 'Edit Quote' : 'New Quote'}</h2>
      </div>

      <div className="form-section" style={{ display:'grid', gridTemplateColumns:'1fr 460px', gap: 12 }}>
        {/* Left panel: form + items */}
        <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, padding: 12, background:'#fff' }}>
          {/* moved Customer / Date / POS to right panel */}

          {/* Terms / Notes */}
          <div className="form-row">
            <div className="form-group">
              <label style={labelStyle}>Terms</label>
              <textarea className="form-control" style={controlStyle} rows={3} placeholder="e.g., Payment due in 15 days" title="Terms and conditions shown on the quote" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
            </div>
            <div className="form-group">
              <label style={labelStyle}>Notes</label>
              <textarea className="form-control" style={controlStyle} rows={3} placeholder="Any additional information for the customer" title="Notes for the customer" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          {defaults.loaded && (
            <div style={{ display:'flex', gap:8, alignItems:'center', marginTop: 4 }}>
              {!defaults.exists && ((form.terms || '').trim() || (form.notes || '').trim()) ? (
                <button className="btn btn-secondary" style={smallBtn} onClick={saveDocDefaults}>
                  Save as default for future quotes
                </button>
              ) : null}
              {defaults.exists && ((form.terms !== defaults.terms) || (form.notes !== defaults.notes)) ? (
                <button className="btn btn-secondary" style={smallBtn} onClick={saveDocDefaults}>
                  Update default
                </button>
              ) : null}
            </div>
          )}

          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'8px 0' }} />

          {/* Items */}
          <div>
            <h3 style={{ fontSize: 14, margin: 0 }}>Items</h3>
            <div className="form-subtitle" style={{ fontSize: 12 }}>HSN/SAC and default tax rates are auto-filled from the selected item. Edit item if needed.</div>
            {gst.checked && gst.hideGST && (
              <div className="field-note" style={{ marginTop: 8 }}>
                Add your GSTIN to enable GST tax calculation. 
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input className="form-control" style={controlStyle} placeholder="Enter 15-digit GSTIN" value={gst.gstinInput} onChange={(e)=> setGst(prev => ({ ...prev, gstinInput: e.target.value }))} />
                  <button className="btn btn-primary" style={smallBtn} onClick={saveCompanyGST}>Save</button>
                </div>
              </div>
            )}

            {/* POS-like item listing (search + filter + cards) */}
            <div style={{ marginTop: 10, border:'1px solid #e5e7eb', borderRadius: 10, padding: 10, background:'#fff' }}>
              <div className="form-row" style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'nowrap' }}>
                <div className="form-group" style={{ flex: 1, minWidth: 0, margin: 0 }}>
                  <div style={{ position:'relative' }}>
                    <input
                      aria-label="Search Items"
                      className="form-control"
                      style={{ ...controlStyle, paddingLeft: 30, width:'100%' }}
                      placeholder="Name / description / HSN-SAC"
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                    />
                    <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#6b7280' }}><FiSearch /></span>
                  </div>
                </div>
                <select aria-label="Type" className="form-control" style={{ ...controlStyle, width: 140 }} value={itemType} onChange={(e) => setItemType(e.target.value)}>
                  <option value="all">All</option>
                  <option value="product">Products</option>
                  <option value="service">Services</option>
                </select>
                <button className="btn btn-secondary" style={smallBtn} onClick={() => setItemSearch('')}>Reset</button>
                <button
                  className="btn btn-primary"
                  style={smallBtn}
                  onClick={() => {
                    setEditingItemId(null);
                    setNewItemAssignToIndex(null);
                    setNewItem({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
                    setShowNewItem(true);
                  }}
                  title="Create new item"
                >
                  <FiPlus /> New Item
                </button>
              </div>

              <div style={{ marginTop: 10, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {filteredItems.slice(0, 80).map((it) => (
                  <ItemCard
                    key={it.id}
                    item={it}
                    qtyInCart={quoteQtyForItem.get(String(it.id)) || 0}
                    // Keep pulsing as long as qty > 0 (like POS)
                    pulse={(quoteQtyForItem.get(String(it.id)) || 0) > 0}
                    onAdd={() => addOrIncItemToQuote(it)}
                    onIncrement={() => addOrIncItemToQuote(it)}
                    onDecrement={() => decItemInQuote(it)}
                    onEdit={() => {
                      setNewItem({ ...it });
                      setEditingItemId(it.id);
                      setNewItemAssignToIndex(null);
                      setShowNewItem(true);
                    }}
                  />
                ))}
              </div>
              {filteredItems.length > 80 && (
                <div className="field-note" style={{ marginTop: 8, fontSize: 11 }}>
                  Showing first 80 items. Refine your search to see more.
                </div>
              )}
              {!filteredItems.length && (
                <div style={{ padding: 12, textAlign:'center', color:'#6b7280', fontSize: 12 }}>No items found.</div>
              )}
            </div>
            <div className="field-note" style={{ marginTop: 10, fontSize: 11 }}>
              Add/remove quantities using the cards above. Discounts can be edited from the Summary on the right.
            </div>

            {showNewItem && (
              <EditItemModal
                item={newItem}
                items={items}
                title={editingItemId ? 'Edit Item' : 'Create New Item'}
                onChange={(field, value) => setNewItem(prev => ({ ...prev, [field]: value }))}
                onClose={() => {
                  setShowNewItem(false);
                  setNewItem({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
                  setNewItemAssignToIndex(null);
                  setEditingItemId(null);
                }}
                onSave={async (payload) => {
                  const savePayload = {
                    companyId: Number(companyId),
                    name: payload.name,
                    description: payload.description || null,
                    unit: payload.unit || 'Piece',
                    price: Number(payload.price || 0),
                    hsnsac: payload.hsnsac || null,
                    isService: !!payload.isService,
                    inventory: Number(payload.isService ? 0 : (payload.inventory || 0)),
                    cgstRate: Number(payload.cgstRate || 0),
                    sgstRate: Number(payload.sgstRate || 0),
                    igstRate: Number(payload.cgstRate || 0) + Number(payload.sgstRate || 0),
                    cessRate: Number(payload.cessRate || 0)
                  };
                  try {
                    if (editingItemId) {
                      await api.put(`/api/items/${editingItemId}`, savePayload);
                    } else {
                      const { data: created } = await api.post('/api/items', savePayload);
                      setItems(prev => [...prev, created]);
                      if (newItemAssignToIndex != null) {
                        setLines(prev => prev.map((ln, i) => i === newItemAssignToIndex ? {
                          ...ln,
                          itemId: created.id,
                          unit: created.unit || ln.unit,
                          price: created.price ?? ln.price,
                          description: ln.description || created.description,
                          cgstRate: ln.cgstRate ?? created.cgstRate,
                          sgstRate: ln.sgstRate ?? created.sgstRate,
                          igstRate: Number((ln.cgstRate ?? created.cgstRate) || 0) + Number((ln.sgstRate ?? created.sgstRate) || 0),
                          cessRate: ln.cessRate ?? created.cessRate
                        } : ln));
                      }
                    }
                    // Refresh items after any create/edit
                    try {
                      const { data: its } = await api.get('/api/items', { params: { companyId } });
                      setItems(its);
                    } catch (e) { /* silent */ }
                  } catch (e) {
                    console.error('Save item failed', e);
                  }
                  setShowNewItem(false);
                  setNewItem({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
                  setNewItemAssignToIndex(null);
                  setEditingItemId(null);
                }}
              />
            )}
          </div>
        </div>

        {/* Right panel: totals + save */}
        <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, background:'#fff', position:'sticky', top: 12, alignSelf:'start', maxHeight:'calc(100vh - 24px)', display:'flex', flexDirection:'column' }}>
          <div style={{ padding: 12, overflowY:'auto', flex: 1 }}>
          {/* Date / Customer / POS */}
          <div className="form-group">
            <label style={labelStyle}>Date</label>
            <input className="form-control" style={controlStyle} type="date" title="Quotation date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <label style={labelStyle}>Customer</label>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ position:'relative', flex:1 }}>
                <input
                  className="form-control"
                  style={{ ...controlStyle, width: '100%' }}
                  placeholder="Search customer"
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  name="customerSearch"
                  value={customerSearchQuery || (customers.find(c => String(c.id) === String(form.customerId))?.name || '')}
                  onChange={(e) => { setCustomerSearchQuery(e.target.value); setCustomerSearchOpen(true); }}
                  onFocus={() => setCustomerSearchOpen(true)}
                  onBlur={() => setTimeout(() => setCustomerSearchOpen(false), 150)}
                  title="Customer selection does not change POS"
                />
                {customerSearchOpen && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e5e7eb', borderTop:'none', zIndex:20, maxHeight:220, overflowY:'auto', borderRadius:'0 0 8px 8px' }}>
                    <div
                      style={{ padding:'8px 10px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:12, color:'#374151' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setForm({ ...form, customerId: '' }); setCustomerSearchQuery(''); setCustomerSearchOpen(false); setShowEditCustomer(false); }}
                    >
                      Clear selection
                    </div>
                    {customers
                      .filter((c) => {
                        const q = customerSearchQuery.trim().toLowerCase();
                        if (!q) return true;
                        return (c.name || '').toLowerCase().includes(q) ||
                               (c.email || '').toLowerCase().includes(q) ||
                               (c.phone || '').toLowerCase().includes(q);
                      })
                      .slice(0, 50)
                      .map((c) => (
                        <div
                          key={c.id}
                          style={{ padding:'8px 10px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:12 }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const val = String(c.id);
                            setForm({ ...form, customerId: val });
                            const derivedPos = resolvePosFromCustomer(c);
                            if (derivedPos) setPosStateCode(derivedPos);
                            const intendedPos = normalizeStateCode(derivedPos || posStateCode);
                            const companyState = normalizeStateCode(companyData?.stateCode);
                            const isInter = companyState && intendedPos && companyState !== intendedPos;
                            setLines(prev => prev.map(l => {
                              const it = items.find(x => String(x.id) === String(l.itemId));
                              if (!it) return l;
                              const lineHasTax = Number(l.cgstRate || 0) > 0 || Number(l.sgstRate || 0) > 0 || Number(l.igstRate || 0) > 0;
                              if (gst.hideGST) return lineHasTax ? l : { ...l, cgstRate: 0, sgstRate: 0, igstRate: 0 };
                              if (isInter) {
                                const ig = inferIgstRateFromLineOrItem(l, it);
                                return { ...l, cgstRate: 0, sgstRate: 0, igstRate: ig };
                              }
                              let cg = Number(it.cgstRate || 0);
                              let sg = Number(it.sgstRate || 0);
                              if ((cg === 0 && sg === 0)) {
                                const srcIg = inferIgstRateFromLineOrItem(l, it);
                                const split = splitIgstRate(srcIg);
                                cg = split.cg; sg = split.sg;
                              }
                              return { ...l, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
                            }));
                            setCustomerSearchQuery('');
                            setCustomerSearchOpen(false);
                          }}
                        >
                          <div style={{ fontWeight:600, color:'#111827' }}>{c.name}</div>
                          <div style={{ color:'#6b7280', fontSize:11 }}>{c.email || '-'} • {c.phone || '-'}</div>
                        </div>
                      ))}
                    {customers.length === 0 && (
                      <div style={{ padding:'8px 10px', fontSize:12, color:'#6b7280' }}>No customers</div>
                    )}
                  </div>
                )}
              </div>
              <button
                className="btn btn-secondary"
                style={{ ...smallBtn, padding:'0 8px', width:32, justifyContent:'center' }}
                title="New customer"
                onClick={() => { setShowCreateCustomer(true); setShowEditCustomer(false); }}
              >
                <FiUserPlus />
              </button>
              <button
                className="btn btn-secondary"
                style={{ ...smallBtn, padding:'0 8px', width:32, justifyContent:'center' }}
                title="Edit customer"
                disabled={!form.customerId}
                onClick={handleOpenEditCustomer}
              >
                <FiEdit2 />
              </button>
            </div>
            {/* Show GST details only within Edit form below; no inline GST row here */}
            {showCreateCustomer && (
              <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginTop:8 }}>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:6 }}>New Customer</div>
                <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                  <input className="form-control" style={controlStyle} placeholder="Name" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
                  <div>
                    <input className="form-control" style={{ ...controlStyle, borderColor: newCustEmailInvalid ? '#ef4444' : undefined }} placeholder="Email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                    {newCustEmailInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a valid email address</div>}
                  </div>
                  <div>
                    <input className="form-control" style={{ ...controlStyle, borderColor: newCustPhoneInvalid ? '#ef4444' : undefined }} placeholder="Phone (10 digits)" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                    {newCustPhoneInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a 10-digit phone number</div>}
                  </div>
                  <input className="form-control" style={controlStyle} placeholder="GSTIN (optional)" value={newCustomer.gstin} onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })} />
                  <input className="form-control" style={controlStyle} placeholder="Address line 1" value={newCustomer.address?.line1 || ''} onChange={(e) => setNewCustomer({ ...newCustomer, address: { ...(newCustomer.address || emptyAddress), line1: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="Address line 2" value={newCustomer.address?.line2 || ''} onChange={(e) => setNewCustomer({ ...newCustomer, address: { ...(newCustomer.address || emptyAddress), line2: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="City" value={newCustomer.address?.city || ''} onChange={(e) => setNewCustomer({ ...newCustomer, address: { ...(newCustomer.address || emptyAddress), city: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="State" value={newCustomer.address?.state || ''} onChange={(e) => setNewCustomer({ ...newCustomer, address: { ...(newCustomer.address || emptyAddress), state: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="Pincode" value={newCustomer.address?.pincode || ''} onChange={(e) => setNewCustomer({ ...newCustomer, address: { ...(newCustomer.address || emptyAddress), pincode: e.target.value } })} />
                </div>
                <div style={{ marginTop:8, display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary" style={smallBtn} onClick={() => setShowCreateCustomer(false)}>Cancel</button>
                  <button className="btn btn-primary" style={smallBtn} disabled={creatingCustomer || !newCustomer.name.trim() || newCustEmailInvalid || newCustPhoneInvalid} onClick={handleCreateCustomer}>
                    {creatingCustomer ? 'Saving…' : 'Create'}
                  </button>
                </div>
              </div>
            )}
            {showEditCustomer && (
              <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginTop:8 }}>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:6 }}>Edit Customer</div>
                <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                  <input className="form-control" style={controlStyle} placeholder="Name" value={editCustomer.name} onChange={(e) => setEditCustomer({ ...editCustomer, name: e.target.value })} />
                  <div>
                    <input className="form-control" style={{ ...controlStyle, borderColor: editCustEmailInvalid ? '#ef4444' : undefined }} placeholder="Email" value={editCustomer.email} onChange={(e) => setEditCustomer({ ...editCustomer, email: e.target.value })} />
                    {editCustEmailInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a valid email address</div>}
                  </div>
                  <div>
                    <input className="form-control" style={{ ...controlStyle, borderColor: editCustPhoneInvalid ? '#ef4444' : undefined }} placeholder="Phone (10 digits)" value={editCustomer.phone} onChange={(e) => setEditCustomer({ ...editCustomer, phone: e.target.value })} />
                    {editCustPhoneInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a 10-digit phone number</div>}
                  </div>
                  <input className="form-control" style={controlStyle} placeholder="GSTIN (optional)" value={editCustomer.gstin} onChange={(e) => setEditCustomer({ ...editCustomer, gstin: e.target.value })} />
                  <input className="form-control" style={controlStyle} placeholder="Address line 1" value={editCustomer.address?.line1 || ''} onChange={(e) => setEditCustomer({ ...editCustomer, address: { ...(editCustomer.address || emptyAddress), line1: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="Address line 2" value={editCustomer.address?.line2 || ''} onChange={(e) => setEditCustomer({ ...editCustomer, address: { ...(editCustomer.address || emptyAddress), line2: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="City" value={editCustomer.address?.city || ''} onChange={(e) => setEditCustomer({ ...editCustomer, address: { ...(editCustomer.address || emptyAddress), city: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="State" value={editCustomer.address?.state || ''} onChange={(e) => setEditCustomer({ ...editCustomer, address: { ...(editCustomer.address || emptyAddress), state: e.target.value } })} />
                  <input className="form-control" style={controlStyle} placeholder="Pincode" value={editCustomer.address?.pincode || ''} onChange={(e) => setEditCustomer({ ...editCustomer, address: { ...(editCustomer.address || emptyAddress), pincode: e.target.value } })} />
                </div>
                <div style={{ marginTop:8, display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary" style={smallBtn} onClick={() => setShowEditCustomer(false)}>Cancel</button>
                  <button className="btn btn-primary" style={smallBtn} onClick={handleSaveEditCustomer} disabled={editCustEmailInvalid || editCustPhoneInvalid || !editCustomer.name.trim()}>
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <label style={labelStyle}>POS (State)</label>
            <select className="form-control" style={controlStyle} title="Select GST state for POS" value={posStateCode} onChange={(e)=> {
              const val = e.target.value;
              setPosStateCode(val);
              const companyState = normalizeStateCode(companyData?.stateCode);
              const nextPos = normalizeStateCode(val);
              const isInter = companyState && nextPos && companyState !== nextPos;
              setLines(prev => prev.map(l => {
                const it = items.find(x => String(x.id) === String(l.itemId));
                if (!it) return l;
                // Check if line already has tax rates - preserve them even if hideGST
                const lineHasTax = Number(l.cgstRate || 0) > 0 || Number(l.sgstRate || 0) > 0 || Number(l.igstRate || 0) > 0;
                if (gst.hideGST && !lineHasTax) return { ...l, cgstRate: 0, sgstRate: 0, igstRate: 0 };
                const hasAnyExplicit = (l.cgstRate != null && l.cgstRate !== '') || (l.sgstRate != null && l.sgstRate !== '') || (l.igstRate != null && l.igstRate !== '');
                if (hasAnyExplicit) {
                  if (isInter) {
                    const ig = inferIgstRateFromLineOrItem(l, it);
                    return { ...l, cgstRate: 0, sgstRate: 0, igstRate: ig };
                  }
                  const hadIgst = Number(l.igstRate || 0) > 0;
                  const cgEmptyOrZero = l.cgstRate == null || l.cgstRate === '' || Number(l.cgstRate) === 0;
                  const sgEmptyOrZero = l.sgstRate == null || l.sgstRate === '' || Number(l.sgstRate) === 0;
                  const shouldFallbackToItem = hadIgst && cgEmptyOrZero && sgEmptyOrZero;
                  const deriveCgstSgst = () => {
                    if (!shouldFallbackToItem) {
                      return {
                        cg: (l.cgstRate != null && l.cgstRate !== '') ? Number(l.cgstRate) : Number(it.cgstRate || 0),
                        sg: (l.sgstRate != null && l.sgstRate !== '') ? Number(l.sgstRate) : Number(it.sgstRate || 0),
                      };
                    }
                    const itemCg = Number(it.cgstRate || 0);
                    const itemSg = Number(it.sgstRate || 0);
                    if (itemCg > 0 || itemSg > 0) return { cg: itemCg, sg: itemSg };
                    const srcIg = inferIgstRateFromLineOrItem(l, it);
                    return splitIgstRate(srcIg);
                  };
                  const { cg, sg } = deriveCgstSgst();
                  return { ...l, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
                }
                if (isInter) {
                  const ig = inferIgstRateFromLineOrItem(l, it);
                  return { ...l, cgstRate: 0, sgstRate: 0, igstRate: ig };
                }
                let cg = Number(it.cgstRate || 0);
                let sg = Number(it.sgstRate || 0);
                if ((cg === 0 && sg === 0)) {
                  const srcIg = inferIgstRateFromLineOrItem(l, it);
                  const split = splitIgstRate(srcIg);
                  cg = split.cg; sg = split.sg;
                }
                return { ...l, cgstRate: cg, sgstRate: sg, igstRate: cg + sg };
              }));
            }}>
              <option value="">Select State</option>
              {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </select>
            <div className="field-note" style={{ fontSize: 11 }}>POS (Place of Supply) determines CGST+SGST vs IGST. You can override it here.</div>
          </div>

        

          <div style={{ fontWeight: 700, fontSize: 14 }}>Totals</div>
          <div className="field-note" style={{ fontSize: 11 }}>Supply Type: {computed.supplyType === 'INTER' ? 'Interstate (IGST)' : 'Intrastate (CGST + SGST)'} - These figures are computed from your line items.</div>
          <div className="field-note" style={{ marginTop: 8, fontSize: 11 }}>
            <div>Company GSTIN: {companyData?.gstin || '-'} | State Code: {companyData?.stateCode || '-'}</div>
            {(() => {
              const cust = customers.find(c => String(c.id) === String(form.customerId));
              return <div>Customer GSTIN: {cust?.gstin || '-'} | State Code: {posStateCode || cust?.stateCode || '-'}</div>;
            })()}
          </div>
          {lines.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, color:'#374151' }}>Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 0.9fr 0.9fr 72px', gap: 6, fontSize: 12, border:'1px solid #e5e7eb', borderRadius:8, padding:8, background:'#fafafa' }}>
                <div style={{ fontWeight: 600, color:'#6b7280' }}>Item</div>
                <div style={{ fontWeight: 600, color:'#6b7280' }}>Qty</div>
                <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Disc</div>
                <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Net</div>
                <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Edit</div>
                {lines.map((l, idx) => {
                  if (!l.itemId || Number(l.quantity || 0) <= 0) return null;
                  const it = items.find(x => String(x.id) === String(l.itemId));
                  const name = it?.name || l.description || '-';
                  const qty = l.quantity ?? '';
                  const price = l.price ?? '';
                  const gross = (Number(qty) || 0) * (Number(price) || 0);
                  const discAmt = Math.max(0, Number(l.discountAmount || 0));
                  const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
                  const discFromPct = (discPct / 100) * gross;
                  const lineDisc = Math.min(gross, discAmt + discFromPct);
                  const netBase = Math.max(0, gross - lineDisc);
                  const netTotal = Number(computed.netBeforeDocDiscount || 0);
                  const docDisc = Number(computed.docDiscount || 0);
                  const share = netTotal > 0 ? (netBase / netTotal) : 0;
                  const docShare = +(docDisc * share).toFixed(2);
                  const totalDisc = +(lineDisc + docShare).toFixed(2);
                  const netAfter = +Math.max(0, netBase - docShare).toFixed(2);
                  const discKey = String(l.itemId);
                  const currentDiscType = (Number(l.discountPercent || 0) > 0 && Number(l.discountAmount || 0) === 0) ? 'percent' : 'amount';
                  const discValue = currentDiscType === 'percent' ? (l.discountPercent ?? '') : (l.discountAmount ?? '');
                  return (
                    <React.Fragment key={discKey}>
                      <div>{name}</div>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control"
                        style={{ ...controlStyle, width: '100%', textAlign: 'right', padding: '2px 6px', height: 28, minHeight: 28 }}
                        value={qty}
                        onChange={(e) => {
                          const next = Math.max(0, parseInt(e.target.value || '0', 10));
                          if (next <= 0) {
                            removeLine(idx);
                            return;
                          }
                          updateLine(idx, { quantity: next });
                        }}
                        aria-label={`Quantity for ${name}`}
                      />
                      <div style={{ textAlign: 'right' }}>{totalDisc || 0}</div>
                      <div style={{ textAlign: 'right', fontWeight: 600 }}>{netAfter}</div>
                      <div style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: 11, padding:'4px 8px', height: 28, minHeight: 28, borderRadius: 8 }}
                          onClick={() => setOpenDisc((m) => ({ ...m, [discKey]: !m[discKey] }))}
                          title="Edit item discount"
                        >
                          + Disc
                        </button>
                      </div>
                      {!!openDisc[discKey] && (
                        <div style={{ gridColumn: '1 / -1', background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 110px 90px', gap:8, alignItems:'center' }}>
                            <div style={{ fontSize: 11, color:'#6b7280' }}>Discount for {name}</div>
                            <input
                              className="form-control"
                              style={{ ...controlStyle, width: '100%', textAlign:'right' }}
                              type="number"
                              min="0"
                              step={currentDiscType === 'percent' ? '1' : '0.01'}
                              placeholder={currentDiscType === 'percent' ? 'Disc %' : 'Disc Amt'}
                              title={currentDiscType === 'percent' ? 'Discount percentage' : 'Flat discount amount'}
                              value={discValue}
                              onChange={(e) => {
                                const v = clamp(e.target.value);
                                if (currentDiscType === 'percent') updateLine(idx, { discountPercent: v, discountAmount: 0 });
                                else updateLine(idx, { discountAmount: v, discountPercent: 0 });
                              }}
                            />
                            <select
                              className="form-control"
                              style={{ ...controlStyle, width: '100%' }}
                              value={currentDiscType}
                              onChange={(e) => {
                                const nextType = e.target.value;
                                const raw = Number(discValue || 0);
                                if (nextType === 'percent') {
                                  const pct = Math.max(0, Math.min(100, raw));
                                  updateLine(idx, { discountPercent: pct, discountAmount: 0 });
                                } else {
                                  updateLine(idx, { discountAmount: Math.max(0, raw), discountPercent: 0 });
                                }
                              }}
                              aria-label="Discount type"
                            >
                              <option value="amount">₹</option>
                              <option value="percent">% </option>
                            </select>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div style={{ borderTop:'1px dashed #e5e7eb', margin:'8px 0' }} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
            <div style={{ color:'#6b7280', fontSize: 12 }}>Discount</div>
            <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:6 }}>
              <input
                className="form-control"
                style={{ ...controlStyle, width: 90, textAlign:'right' }}
                type="number"
                min="0"
                placeholder="Discount"
                step={discountType === 'percent' ? '1' : '0.01'}
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
              <select
                className="form-control"
                style={{ ...controlStyle, width: 90 }}
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                aria-label="Discount type"
              >
                <option value="amount">₹</option>
                <option value="percent">% </option>
              </select>
            </div>
          </div>
          {Number(docDiscountAmount || 0) > 0 && (
            <div className="field-note" style={{ fontSize: 11, marginTop: 4 }}>
              Doc discount applied before tax: {docDiscountAmount}
            </div>
          )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, marginTop: 8 }}>
              {/* Quote discount (like POS) */}
         
            <div style={{ color:'#6b7280' }}>Total</div><div style={{ textAlign: 'right', fontWeight: 600 }}>{computed.grossTotal ?? (computed.discountTotal + computed.netBeforeDocDiscount)}</div>
            <div style={{ color:'#6b7280' }}>Item Discounts</div><div style={{ textAlign: 'right' }}>{computed.lineDiscountTotal ?? 0}</div>
            <div style={{ color:'#6b7280' }}>Doc Discount</div><div style={{ textAlign: 'right' }}>{computed.docDiscount ?? 0}</div>
            <div style={{ color:'#6b7280' }}>Taxable Value</div><div style={{ textAlign: 'right' }}>{computed.taxableValue}</div>
            {!gst.hideGST && <><div style={{ color:'#6b7280' }}>CGST</div><div style={{ textAlign: 'right' }}>{computed.cgstAmount}</div></>}
            {!gst.hideGST && <><div style={{ color:'#6b7280' }}>SGST</div><div style={{ textAlign: 'right' }}>{computed.sgstAmount}</div></>}
            {!gst.hideGST && <><div style={{ color:'#6b7280' }}>IGST</div><div style={{ textAlign: 'right' }}>{computed.igstAmount}</div></>}
            {!gst.hideGST && <><div style={{ color:'#6b7280' }}>Cess</div><div style={{ textAlign: 'right' }}>{computed.cessAmount}</div></>}
            <div style={{ fontWeight: 600 }}>Grand Total</div><div style={{ textAlign: 'right', fontWeight: 700 }}>{computed.total}</div>
          </div>
          </div>
          {/* Sticky save button at bottom of right panel */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e5e7eb', background: '#fff', borderRadius: '0 0 10px 10px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={smallBtn} onClick={save} disabled={!form.customerId || lines.filter(l => l.itemId && Number(l.quantity || 0) > 0).length === 0}>
              Save Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

